import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteContractorProposal,
  listContractorProposals,
} from "../../services/contractorService";
import { HiOutlineDotsHorizontal } from "react-icons/hi";
import { renderCurrencyIconColored } from "../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";

const StyledHeading = styled(Typography)(() => ({
  fontSize: "24px",
  fontWeight: 600,
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  letterSpacing: 0,
}));

const StyledButton = styled(Button)(() => ({
  background: "#4D6BF6",
  color: "#ffffff",
  boxShadow: "none",
  border: "1px solid #4D6BF6",
  whiteSpace: "nowrap",
  fontSize: "15px",
  fontWeight: 600,
  borderRadius: "10px",
  padding: ".5vh .7vw",
  letterSpacing: 0,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#3B5BDB",
    borderColor: "#3B5BDB",
    boxShadow: "none",
  },
}));

const StyledTabs = styled(Tabs)({
  display: "flex",
  gap: "10px",
  padding: "0 2vw",
  minHeight: "40px",
  "& .MuiTabs-indicator": {
    background: "none",
  },
  "& .MuiTabs-flexContainer": {
    gap: "10px",
  },
});

const StyledTab = styled(Tab)(({ selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0d0d0d" : "#fff",
  border: "1px solid #d7d8d9",
  borderRadius: "10px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: 0,
  padding: "4px 16px !important",
  fontFamily: "Inter Tight",
  fontSize: "14px",
  "&:hover": {
    backgroundColor: selected ? "#0d0d0d" : "#F7F7F7",
    color: !selected ? "#000" : undefined,
  },
}));

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
  fontFamily: "Inter Tight",
}));

const RFC_TABS = [
  { id: "all", label: "All", status: "" },
  { id: "draft", label: "Draft", status: "DRAFT" },
  { id: "submitted", label: "Submitted", status: "proposed-to-client" },
  { id: "accepted", label: "Won", status: "ACCEPTED" },
  { id: "rejected", label: "Lost", status: "REJECTED" },
  // {
  //   id: "open_for_modification",
  //   label: "Open For Modification",
  //   status: "OPEN_FOR_MODIFICATION",
  // },
];

// Dot color per raw backend status — the API mixes uppercase enums
// (DRAFT / ACCEPTED / REJECTED) and kebab-case states
// (proposed-to-client / ongoing). We normalize to lowercase before
// looking up so both shapes hit the same entry.
const STATUS_DOT_COLORS = {
  draft: "#d46b08",
  "proposed-to-client": "rgb(56, 158, 13)", // dark green
  submitted: "rgb(56, 158, 13)",
  ongoing: "#096dd9", // blue
  accepted: "#7C3AED", // purple
  won: "#7C3AED",
  rejected: "#F59E0B", // yellow
  lost: "#F59E0B",
  open_for_modification: "#c41d7f",
};

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const getStatusDotColor = (status) =>
  STATUS_DOT_COLORS[normalizeStatus(status)] || "#9CA3AF";

const formatStatus = (status) => {
  if (!status) return "—";
  return String(status)
    .replace(/[_-]/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Status indicator — colored dot + capitalized label. Reverts the
// pill-style chip; the dot uses the per-status palette defined above.
const StatusIndicator = ({ status, dotSize = 12 }) => {
  if (!status) return <span>—</span>;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: dotSize,
          height: dotSize,
          borderRadius: "50%",
          backgroundColor: getStatusDotColor(status),
          border: "2px solid #E5E7EB",
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: 13,
          color: "#0D0D0E",
          fontFamily: "Inter Tight",
          fontWeight: 500,
        }}
      >
        {formatStatus(status)}
      </Typography>
    </Box>
  );
};

// Deterministic palette for the customer avatar — pick a color based on
// the customer name so the same customer is always the same color across
// rows / sessions, but two customers in the same list are still visually
// distinguishable.
const AVATAR_PALETTE = [
  { bg: "#E0F2FE", fg: "#0369A1" }, // blue
  { bg: "#DCFCE7", fg: "#15803D" }, // green
  { bg: "#F3E8FF", fg: "#7E22CE" }, // purple
  { bg: "#FEF3C7", fg: "#B45309" }, // amber
  { bg: "#FCE7F3", fg: "#BE185D" }, // pink
  { bg: "#FFE4E6", fg: "#BE123C" }, // rose
  { bg: "#CCFBF1", fg: "#0F766E" }, // teal
  { bg: "#FEE2E2", fg: "#B91C1C" }, // red
];

const pickAvatarColors = (name) => {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
};

const CustomerAvatar = ({ name }) => {
  const display = String(name || "").trim();
  if (!display) {
    return (
      <Typography
        sx={{ color: "#9CA3AF", fontSize: 13, fontFamily: "Inter Tight" }}
      >
        —
      </Typography>
    );
  }
  const letter = display.charAt(0).toUpperCase();
  const { bg, fg } = pickAvatarColors(display);
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: bg,
          color: fg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "Inter Tight",
          flexShrink: 0,
        }}
      >
        {letter}
      </Box>
      <Tooltip title={display} arrow>
        <Typography
          sx={{
            fontSize: 13,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 180,
          }}
        >
          {display}
        </Typography>
      </Tooltip>
    </Box>
  );
};

const RFCs = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const openMenu = (e, item) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuItem(item);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  const activeTab = RFC_TABS[tabIndex];

  const fetchProposals = async () => {
    setLoading(true);
    try {
      const res = await listContractorProposals({
        status: activeTab.status || undefined,
      });
      const data = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.proposals)
            ? res.data.proposals
            : Array.isArray(res?.proposals)
              ? res.proposals
              : [];
      setItems(data);
    } catch (err) {
      console.error("Failed to load proposals", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex]);

  const handleAddProposal = () => navigate("/contractor/proposal/new");

  const handleEdit = (item) => {
    const proposalId = item?.proposal_id || item?.id;
    if (proposalId) {
      navigate(`/contractor/proposal/${proposalId}`);
    } else {
      navigate("/contractor/proposal/new");
    }
  };

  // Row click → contractor project details (Overview tab by default).
  // Path segment carries the proposal id; the project_id query param
  // carries the project id and feeds the stage-aware data fetches in
  // ContractorProjectDetails.
  const handleViewProject = (item) => {
    const proposalId = item?.proposal_id || item?.id;
    const projectId = item?.project_id || item?.projectId || proposalId;
    if (!proposalId) return;
    const qs = new URLSearchParams({
      managed_by: "contractor",
      project_id: String(projectId),
    });
    navigate(`/project/details/${proposalId}?${qs.toString()}`);
  };

  // Edit + Delete are only valid for DRAFT proposals — once submitted/lost/won
  // the proposal is locked.
  const isDraftItem = (item) =>
    (item?.proposal_status || item?.status || "").toUpperCase() === "DRAFT";

  const handleDelete = (item) => {
    setDeleteTarget(item);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    const proposalId = deleteTarget?.proposal_id || deleteTarget?.id;
    if (!proposalId) {
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      await deleteContractorProposal(proposalId);
      toast.success("Proposal deleted");
      setDeleteTarget(null);
      fetchProposals();
    } catch (e) {
      toast.error(e?.message || "Failed to delete proposal");
    } finally {
      setDeleting(false);
    }
  };

  const showEmpty = !loading && items.length === 0;

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: { xs: "2vh 4vw 2vh 4vw", md: "4vh 2vw 2vh 2vw" },
          gap: 1,
          background: {
            xs: "none",
            md: "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
          },
        }}
      >
        <StyledHeading sx={{ fontSize: { xs: "22px", md: "24px" } }}>
          Proposals
        </StyledHeading>
        <StyledButton variant="contained" onClick={handleAddProposal}>
          Create Proposal
        </StyledButton>
      </Box>

      <StyledTabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        variant="scrollable"
        scrollButtons={false}
      >
        {RFC_TABS.map((t, i) => (
          <StyledTab key={t.id} label={t.label} selected={tabIndex === i} />
        ))}
      </StyledTabs>

      <Box
        sx={{
          flex: 1,
          padding: { xs: "1vh 4vw 2vh 4vw", md: "4vh 2vw 2vh 2vw" },
          minHeight: 0,
          overflow: "auto",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : showEmpty ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              maxWidth: 480,
              margin: "4vh auto 0",
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                color: "#949daa",
                fontFamily: "Inter Tight",
              }}
            >
              No data available
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box>
            {items.map((item) => {
              const rawStatus = item?.proposal_status || item?.status || "";
              const projectName =
                item?.project_name || item?.name || "Untitled";
              const proposalId = item?.proposal_id || item?.id;
              const customerName = item?.customer_name || "";
              const location = item?.location_address || "";
              const valueRaw = item?.value ?? null;
              return (
                <Card
                  key={proposalId || projectName}
                  onClick={() => handleViewProject(item)}
                  sx={{
                    margin: "0 !important",
                    borderRadius: 0,
                    boxShadow: "none",
                    borderBottom: "1px solid #D7D8D9",
                    cursor: "pointer",
                  }}
                >
                  <CardContent
                    sx={{
                      padding: "16px !important",
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 600,
                          color: "#0D0D0E",
                          fontFamily: "Inter Tight",
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {projectName}
                      </Typography>
                      {isDraftItem(item) && (
                        <IconButton
                          size="small"
                          onClick={(e) => openMenu(e, item)}
                          sx={{ color: "#0D0D0E", flexShrink: 0, p: 0.5 }}
                        >
                          <HiOutlineDotsHorizontal size={20} />
                        </IconButton>
                      )}
                    </Box>

                    <div style={{ display: "flex" }}>
                      <Box style={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#949DAA",
                            fontFamily: "Inter Tight",
                            fontWeight: 500,
                            mb: 0.5,
                          }}
                        >
                          Customer
                        </Typography>
                        <CustomerAvatar name={customerName} />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          justifyContent: "flex-end",
                          display: "flex",
                        }}
                      >
                        <StatusIndicator status={rawStatus} />
                      </Box>
                    </div>

                    <div style={{ display: "flex" }}>
                      <Box style={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#949DAA",
                            fontFamily: "Inter Tight",
                            fontWeight: 500,
                          }}
                        >
                          Location
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "#0D0D0E",
                            fontFamily: "Inter Tight",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {location}
                        </Typography>
                      </Box>

                      {valueRaw != null && valueRaw !== "" && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 13,
                            color: "#0D0D0E",
                            fontFamily: "Inter Tight",
                            fontWeight: 600,
                            flex: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          {renderCurrencyIconColored("black-small")}
                          {formattedAmountWithoutAed(valueRaw)}
                        </Box>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ) : (
          <TableContainer
            sx={{
              boxShadow: "none",
              backgroundColor: "transparent",
            }}
          >
            <Table
              sx={{
                "& thead th": {
                  color: "#949DAA",
                  fontWeight: 500,
                  fontSize: 13,
                },
                "& tbody tr": {
                  borderBottom: "none",
                },
                "& tbody tr:hover": {
                  backgroundColor: "#F7F7F7",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Proposal Name</StyledTableCell>
                  <StyledTableCell>Customer</StyledTableCell>
                  <StyledTableCell>Location</StyledTableCell>
                  <StyledTableCell align="right">
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.5,
                      }}
                    >
                      Proposal Value
                      
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell align="right">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => {
                  const rawStatus = item?.proposal_status || item?.status || "";
                  const projectName =
                    item?.project_name || item?.name || "Untitled";
                  const proposalId = item?.proposal_id || item?.id;
                  const customerName = item?.customer_name || "";
                  const location = item?.location_address || "";
                  const valueRaw = item?.value ?? null;
                  return (
                    <TableRow
                      key={proposalId || projectName}
                      onClick={() => handleViewProject(item)}
                      sx={{ cursor: "pointer" }}
                    >
                      <StyledTableCell>
                        <Tooltip title={projectName} arrow>
                          <Box
                            onClick={() => handleViewProject(item)}
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                              color: "#0D0D0E",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: 260,
                              fontFamily: "Inter Tight",
                              cursor: "pointer",
                              "&:hover": { color: "#4D6BF6" },
                              textTransform: "capitalize"
                            }}
                          >
                            {projectName}
                          </Box>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell>
                        <CustomerAvatar name={customerName} />
                      </StyledTableCell>
                      <StyledTableCell sx={{ color: "#0D0D0E", fontSize: 13 }}>
                        <Tooltip title={location || ""} arrow>
                          <Box
                            sx={{
                              maxWidth: 260,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              color: location ? "#0D0D0E" : "#9CA3AF",
                              fontFamily: "Inter Tight",
                            }}
                          >
                            {location || "—"}
                          </Box>
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell
                        align="right"
                        sx={{
                          color: "#0D0D0E",
                          fontSize: 13,
                          fontFamily: "Inter Tight",
                        }}
                      >
                        {valueRaw != null && valueRaw !== "" ? (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 0.5,
                            }}
                          >
                            {renderCurrencyIconColored("black-small")}
                            {formattedAmountWithoutAed(valueRaw)}
                          </Box>
                        ) : (
                          "—"
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusIndicator status={rawStatus} />
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {isDraftItem(item) && (
                          <IconButton
                            size="small"
                            onClick={(e) => openMenu(e, item)}
                            sx={{ color: "#0D0D0E" }}
                          >
                            <HiOutlineDotsHorizontal size={20} />
                          </IconButton>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "0 4px 16px rgba(13, 13, 14, 0.10)",
            border: "1px solid #EEF0F2",
            minWidth: 140,
            mt: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuItem) handleEdit(menuItem);
            closeMenu();
          }}
          sx={{ fontFamily: "Inter Tight", fontSize: 14, color: "#0D0D0E" }}
        >
          <ListItemIcon sx={{ minWidth: "28px !important" }}>
            <EditIcon fontSize="small" sx={{ color: "#0D0D0E" }} />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ fontSize: 14, fontFamily: "Inter Tight" }}
          >
            Edit
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuItem) handleDelete(menuItem);
            closeMenu();
          }}
          sx={{ fontFamily: "Inter Tight", fontSize: 14, color: "#DC2626" }}
        >
          <ListItemIcon sx={{ minWidth: "28px !important" }}>
            <MdDelete size={16} color="#DC2626" />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{
              fontSize: 14,
              fontFamily: "Inter Tight",
              color: "#DC2626",
            }}
          >
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteModal}
        PaperProps={{
          sx: { borderRadius: "12px", width: "min(420px, 92vw)" },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Inter Tight",
            fontSize: 18,
            fontWeight: 600,
            color: "#0D0D0E",
            pb: 0.5,
          }}
        >
          Delete Proposal?
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography
            sx={{
              fontSize: 14,
              color: "#4B5563",
              fontFamily: "Inter Tight",
              mb: 2,
            }}
          >
            Are you sure you want to delete this proposal? This action cannot be
            undone.
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              backgroundColor: "#F9FAFB",
            }}
          >
            {deleteTarget?.image_url || deleteTarget?.project_image ? (
              <Box
                component="img"
                src={
                  deleteTarget?.image_url ||
                  (Array.isArray(deleteTarget?.project_image)
                    ? deleteTarget.project_image[0]?.image
                    : deleteTarget?.project_image)
                }
                alt={deleteTarget?.project_name || "Proposal"}
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  flexShrink: 0,
                  backgroundColor: "#E5E7EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9CA3AF",
                }}
              >
                <ImageOutlinedIcon />
              </Box>
            )}
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {deleteTarget?.project_name || deleteTarget?.name || "Untitled"}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={closeDeleteModal}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#0D0D0E",
              border: "1px solid #D7D8D9",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontFamily: "Inter Tight",
              color: "#fff",
              backgroundColor: "#DC2626",
              borderRadius: "10px",
              px: 2,
              "&:hover": { backgroundColor: "#B91C1C" },
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RFCs;
